import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import useSWR from 'swr';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import Select from '@/components/@IntranetLibrary/Select';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { Body1 } from '@/components/@IntranetLibrary/Typography';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import {
  buildSupplierEvaluationContentHtml,
  SupplierEvaluationSnapshot,
} from '@/components/Approval/@components/SupplierEvaluation';
import { getSupplierEvaluationDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildSupplierEvaluationCandidateLabel,
  SUPPLIER_EVALUATION_EMPTY_MESSAGE,
  type SupplierEvaluationDraftSource,
} from '@/utils/approval/supplierEvaluation';
import { FieldBlock, FieldHeader } from './styles';
import type { ApprovalFormValues } from './types';

const CONTROL_HEIGHT = 44;

type Props = { isEditMode: boolean };

/**
 * GERP 협력사 평가(127) — 미상신 건 선택 → 「확인」으로 평가 표를 통째로 교체, 제목은 고른 건 제목으로 덮어씀.
 * 셀렉트만 바꾸면 확정한 표·제목·결재 건은 그대로. 편집은 건을 바꿀 수 없고 저장 본문만
 */
const SupplierEvaluationField = ({ isEditMode }: Props) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const confirmed = useWatch({ control, name: 'supplierEvaluation' });

  const { data: source, isLoading } = useSWR<SupplierEvaluationDraftSource>(
    isEditMode ? null : getSupplierEvaluationDraftSourceUrl(),
    getNestedDataFetcher,
    {
      revalidateOnFocus: false,
      onError: () =>
        snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') }),
    },
  );
  const candidates = source?.display.candidates ?? [];

  const [selectedId, setSelectedId] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  // 확인 중 다른 건을 고르고 다시 확인하면 늦게 온 이전 응답이 본문을 덮지 않게
  const requestIdRef = useRef(0);

  // 레거시 콤보: 목록을 받으면 첫 건 선택
  useEffect(() => {
    if (selectedId || !candidates.length) return;
    setSelectedId(candidates[0].aprvId);
  }, [candidates, selectedId]);

  const handleConfirm = async () => {
    if (isConfirming) return;
    const option = candidates.find((c) => c.aprvId === selectedId);
    if (!option) {
      snackbar.showError({ message: t(SUPPLIER_EVALUATION_EMPTY_MESSAGE) });
      return;
    }
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setIsConfirming(true);
    try {
      const data = await getNestedDataFetcher<SupplierEvaluationDraftSource>(
        getSupplierEvaluationDraftSourceUrl(option.aprvId),
      );
      if (requestId !== requestIdRef.current) return;
      const content = data.display.content;
      if (!content) throw new Error('content missing');
      setValue('subject', buildSupplierEvaluationCandidateLabel(option), {
        shouldDirty: true,
      });
      setValue(
        'supplierEvaluation',
        {
          systemId: option.sysId,
          approvalTypeCode: option.aprvTypcd,
          approvalId: option.aprvId,
          content: buildSupplierEvaluationContentHtml(content, t),
        },
        { shouldDirty: true },
      );
    } catch {
      if (requestId !== requestIdRef.current) return;
      // 표·제목은 이전 건 그대로라 셀렉트도 그 건으로
      const confirmedId = getValues('supplierEvaluation')?.approvalId;
      if (confirmedId) setSelectedId(confirmedId);
      snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
    } finally {
      if (requestId === requestIdRef.current) setIsConfirming(false);
    }
  };

  const body = confirmed?.content ? (
    <SupplierEvaluationSnapshot content={confirmed.content} />
  ) : null;

  const header = (
    <FieldHeader>
      <Body1>{t('업체성과평가')}</Body1>
    </FieldHeader>
  );

  if (isEditMode)
    return (
      <FieldBlock>
        {header}
        {body}
      </FieldBlock>
    );

  return (
    <FieldBlock>
      {header}
      <ControlRow>
        <Select
          value={selectedId}
          options={candidates.map((c) => ({
            value: c.aprvId,
            label: buildSupplierEvaluationCandidateLabel(c),
          }))}
          placeholder={
            isLoading
              ? t('불러오는 중...')
              : candidates.length
              ? t('결재할 건을 선택해주세요')
              : t('진행할 내역이 없습니다.')
          }
          onChange={(e) => setSelectedId(String(e.target.value))}
          sx={{
            ...approvalOutlineSelectSx,
            flex: 1,
            minWidth: 0,
            height: CONTROL_HEIGHT,
          }}
        />
        <BoxButton
          type="button"
          variant="keyColor"
          size="medium"
          disabled={isLoading || isConfirming}
          startIcon={
            isConfirming ? <Spinner theme="white" size="16px" /> : undefined
          }
          onClick={handleConfirm}
        >
          {t('확인')}
        </BoxButton>
      </ControlRow>
      {body}
    </FieldBlock>
  );
};

const ControlRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
});

export default SupplierEvaluationField;
